import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./AdminProducts.css";
import { resolveImageUrl } from "../utils/resolveImageUrl";
import getApiBase from "../utils/apiBase";
import Seo from "../components/Seo";
import { useI18n } from "../i18n/I18nContext";
import { PRODUCT_FILTERS, getProductTags, normalizeTagValues } from "./productFilters";

const API_BASE = getApiBase();
const TAG_TRANSLATION_KEYS = {
  "Two-Hand Looping": "store.tag.twoHandLooping",
  "Yo-Yo Parts": "store.tag.parts",
  Offstring: "store.tag.offstring",
  Freehand: "store.tag.freehand",
  "Single-Hand String": "store.tag.singleHandString",
  "Two-Hand String": "store.tag.twoHandString",
};

const emptyProduct = {
  id: "",
  name: "",
  description: "",
  include: "",
  price: "",
  weight: "",
  imageUrl: "",
  videoUrl: "",
  price_id: "",
  tags: {
    brand: ["YCH-YOYO"],
    type: [],
    group: [],
  },
  colors: [],
  customAvailable: false,
  customDescription: "",
  mockupBaseImage: "",
  mockupConfig: {
    centerXRatio: 0.5,
    centerYRatio: 0.5,
    radiusRatio: 0.22,
  },
};

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const cloneProduct = (product) => JSON.parse(JSON.stringify(product));

const parseTagInput = (value) => (
  normalizeTagValues(String(value || "")
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean))
);

const formatTagInput = (values) => (
  Array.isArray(values) ? values.join(", ") : String(values || "")
);

const getEditableTagValues = (tags = {}, tagKey) => {
  const values = tags[tagKey];
  return Array.isArray(values) ? normalizeTagValues(values) : parseTagInput(values);
};

const normalizeEditableTags = (tags = {}) => {
  const normalizedTags = PRODUCT_FILTERS.reduce((result, filter) => {
    const values = Array.isArray(tags[filter.key])
      ? tags[filter.key]
      : parseTagInput(tags[filter.key]);
    result[filter.key] = normalizeTagValues(values);
    return result;
  }, {});

  if (!normalizedTags.brand.length) {
    normalizedTags.brand = ["YCH-YOYO"];
  }

  return normalizedTags;
};

const AdminProducts = () => {
  const { t } = useI18n();
  const displayTag = (value) => Object.prototype.hasOwnProperty.call(TAG_TRANSLATION_KEYS, value) ? t(TAG_TRANSLATION_KEYS[value]) : value;
  const [auth, setAuth] = useState(localStorage.getItem("adminAuth") || "");
  const [login, setLogin] = useState({ username: "", password: "" });
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(() => cloneProduct(emptyProduct));
  const [editingId, setEditingId] = useState(null);
  const [status, setStatus] = useState("");
  const [highlightForm, setHighlightForm] = useState(false);
  const formRef = useRef(null);

  const authHeaders = auth ? { Authorization: `Basic ${auth}` } : {};
  const tagSuggestions = useMemo(() => (
    PRODUCT_FILTERS.reduce((suggestions, filter) => {
      const productTags = products.flatMap((product) => getProductTags(product)[filter.key] || []);
      suggestions[filter.key] = normalizeTagValues([...filter.defaultOptions, ...productTags]);
      return suggestions;
    }, {})
  ), [products]);

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/products`);
      const data = await response.json();
      setProducts(Array.isArray(data) ? data : []);
    } catch (error) {
      setStatus("admin.products.loadFailed");
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleLogin = (event) => {
    event.preventDefault();
    const token = btoa(`${login.username}:${login.password}`);
    localStorage.setItem("adminAuth", token);
    setAuth(token);
    setStatus("admin.loggedIn");
  };

  const handleLogout = () => {
    localStorage.removeItem("adminAuth");
    setAuth("");
    setLogin({ username: "", password: "" });
    setStatus("admin.loggedOut");
  };

  const handleEdit = (product) => {
    setEditingId(product.id);
    setForm({
      ...cloneProduct(emptyProduct),
      ...cloneProduct(product),
      tags: getProductTags(product),
    });
    setStatus("");
    requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    setHighlightForm(true);
    window.setTimeout(() => setHighlightForm(false), 1200);
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(cloneProduct(emptyProduct));
  };

  const buildPayload = () => {
    const basePrice = toNumber(form.price, 0);
    return {
      ...form,
      id: form.id ? toNumber(form.id, undefined) : undefined,
      price: basePrice,
      weight: toNumber(form.weight, 0),
      tags: normalizeEditableTags(form.tags),
      colors: (form.colors || []).map((color) => ({
        name: color.name || "",
        imageUrl: color.imageUrl || "",
        price: toNumber(color.price, basePrice),
      })),
      customAvailable: Boolean(form.customAvailable),
      customDescription: form.customDescription || "",
      mockupBaseImage: form.mockupBaseImage || "",
      mockupConfig: {
        centerXRatio: toNumber(form.mockupConfig?.centerXRatio, 0.5),
        centerYRatio: toNumber(form.mockupConfig?.centerYRatio, 0.5),
        radiusRatio: toNumber(form.mockupConfig?.radiusRatio, 0.22),
      },
    };
  };

  const saveProduct = async () => {
    if (!auth) {
      setStatus("admin.loginRequired");
      return;
    }
    const payload = buildPayload();
    try {
      const response = await fetch(
        editingId
          ? `${API_BASE}/api/admin/products/${editingId}`
          : `${API_BASE}/api/admin/products`,
        {
          method: editingId ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders,
          },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) {
        throw new Error("Save failed.");
      }
      await fetchProducts();
      resetForm();
      setStatus(editingId ? "admin.products.updated" : "admin.products.created");
    } catch (error) {
      setStatus("admin.products.saveFailed");
    }
  };

  const deleteProduct = async (productId) => {
    if (!auth) {
      setStatus("admin.loginRequired");
      return;
    }
    if (!window.confirm(t("admin.products.deleteConfirm"))) {
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/api/admin/products/${productId}`, {
        method: "DELETE",
        headers: authHeaders,
      });
      if (!response.ok) {
        throw new Error("Delete failed.");
      }
      await fetchProducts();
      setStatus("admin.products.deleted");
    } catch (error) {
      setStatus("admin.products.deleteFailed");
    }
  };

  const uploadImage = async (file) => {
    if (!auth) {
      setStatus("admin.loginRequired");
      return "";
    }
    const formData = new FormData();
    formData.append("file", file);
    const response = await fetch(`${API_BASE}/api/admin/upload`, {
      method: "POST",
      headers: authHeaders,
      body: formData,
    });
    if (!response.ok) {
      throw new Error("Upload failed.");
    }
    const data = await response.json();
    return data.url || "";
  };

  const handleMainUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    try {
      const url = await uploadImage(file);
      if (url) {
        setForm((prev) => ({ ...prev, imageUrl: url }));
      }
    } catch (error) {
      setStatus("admin.products.uploadFailed");
    }
  };

  const handleColorUpload = async (index, event) => {
    const file = event.target.files[0];
    if (!file) return;
    try {
      const url = await uploadImage(file);
      if (url) {
        setForm((prev) => {
          const colors = [...(prev.colors || [])];
          colors[index] = { ...colors[index], imageUrl: url };
          return { ...prev, colors };
        });
      }
    } catch (error) {
      setStatus("admin.products.uploadFailed");
    }
  };

  const updateColor = (index, key, value) => {
    setForm((prev) => {
      const colors = [...(prev.colors || [])];
      colors[index] = { ...colors[index], [key]: value };
      return { ...prev, colors };
    });
  };

  const addColor = () => {
    setForm((prev) => ({
      ...prev,
      colors: [...(prev.colors || []), { name: "", imageUrl: "", price: prev.price || "" }],
    }));
  };

  const removeColor = (index) => {
    setForm((prev) => ({
      ...prev,
      colors: (prev.colors || []).filter((_, idx) => idx !== index),
    }));
  };

  const updateTagValues = (tagKey, values) => {
    setForm((prev) => ({
      ...prev,
      tags: {
        ...(prev.tags || {}),
        [tagKey]: values,
      },
    }));
  };

  const toggleTagValue = (tagKey, value) => {
    setForm((prev) => {
      const currentValues = getEditableTagValues(prev.tags, tagKey);
      const nextValues = currentValues.includes(value)
        ? currentValues.filter((item) => item !== value)
        : [...currentValues, value];

      return {
        ...prev,
        tags: {
          ...(prev.tags || {}),
          [tagKey]: nextValues,
        },
      };
    });
  };

  return (
    <div className="admin-products">
      <Seo
        title={t("admin.products.title")}
        description={t("admin.products.description")}
        path="/admin"
        noindex
      />
      <header className="admin-header">
        <div>
          <h1>{t("admin.products.title")}</h1>
          <Link className="admin-nav-link" to="/admin/custom-requests">{t("admin.nav.requests")}</Link>
          <Link className="admin-nav-link" to="/admin/custom-projects">{t("admin.nav.projects")}</Link>
          <Link className="admin-nav-link" to="/admin/orders">{t("admin.nav.orders")}</Link>
          <Link className="admin-nav-link" to="/admin/community">{t("admin.nav.community")}</Link>
          <Link className="admin-nav-link" to="/admin/analytics">
            {t('analytics.nav')}
          </Link>
        </div>
        {auth ? (
          <button onClick={handleLogout}>{t("admin.logout")}</button>
        ) : null}
      </header>
      {status && <p className="status" role="status">{t(status)}</p>}

      {!auth && (
        <form className="login-card" onSubmit={handleLogin}>
          <h2>{t("admin.loginTitle")}</h2>
          <label>
            {t("admin.username")}
            <input
              type="text"
              value={login.username}
              onChange={(event) =>
                setLogin((prev) => ({ ...prev, username: event.target.value }))
              }
            />
          </label>
          <label>
            {t("admin.password")}
            <input
              type="password"
              value={login.password}
              onChange={(event) =>
                setLogin((prev) => ({ ...prev, password: event.target.value }))
              }
            />
          </label>
          <button type="submit">{t("admin.login")}</button>
        </form>
      )}

      {auth && (
        <>
          <section
            className={`form-card${highlightForm ? " form-card-highlight" : ""}`}
            ref={formRef}
          >
            <div className="form-header">
              <h2>{t(editingId ? "admin.products.edit" : "admin.products.new")}</h2>
              {editingId && (
                <button type="button" onClick={resetForm}>{t("admin.products.newAction")}</button>
              )}
            </div>

            <div className="grid">
              <label>
                {t("admin.products.optionalId")}
                <input
                  type="number"
                  value={form.id}
                  onChange={(event) => setForm((prev) => ({ ...prev, id: event.target.value }))}
                />
              </label>
              <label>
                {t("admin.products.name")}
                <input
                  type="text"
                  value={form.name}
                  onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                />
              </label>
              <label>
                {t("admin.products.price")}
                <input
                  type="number"
                  step="0.01"
                  value={form.price}
                  onChange={(event) => setForm((prev) => ({ ...prev, price: event.target.value }))}
                />
              </label>
              <label>
                {t("admin.products.weight")}
                <input
                  type="number"
                  step="0.01"
                  value={form.weight}
                  onChange={(event) => setForm((prev) => ({ ...prev, weight: event.target.value }))}
                />
              </label>
              <label>
                {t("admin.products.videoUrl")}
                <input
                  type="text"
                  value={form.videoUrl}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, videoUrl: event.target.value }))
                  }
                />
              </label>
              <label>
                {t("admin.products.priceId")}
                <input
                  type="text"
                  value={form.price_id}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, price_id: event.target.value }))
                  }
                />
              </label>
            </div>

            <label className="full-width">

              {t("admin.products.includes")}

              <textarea
                rows="2"
                value={form.include}
                onChange={(event) => setForm((prev) => ({ ...prev, include: event.target.value }))}
              />
            </label>
            <label className="full-width">
              {t("admin.products.productDescription")}
              <textarea
                rows="4"
                value={form.description}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, description: event.target.value }))
                }
              />
            </label>

            <div className="custom-admin-section">
              <div className="custom-admin-heading">
                <h3>{t("admin.products.customizerSettings")}</h3>
                <p>{t("admin.products.customizerHelp")}</p>
              </div>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={Boolean(form.customAvailable)}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, customAvailable: event.target.checked }))
                  }
                />
                {t("admin.products.customAvailable")}
              </label>
              <div className="grid">
                <label>
                  {t("admin.products.mockupBaseImage")}
                  <input
                    type="text"
                    value={form.mockupBaseImage || ""}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, mockupBaseImage: event.target.value }))
                    }
                  />
                </label>
                <label>
                  {t("admin.products.centerX")}
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    value={form.mockupConfig?.centerXRatio ?? 0.5}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        mockupConfig: {
                          ...(prev.mockupConfig || {}),
                          centerXRatio: event.target.value,
                        },
                      }))
                    }
                  />
                </label>
                <label>
                  {t("admin.products.centerY")}
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    value={form.mockupConfig?.centerYRatio ?? 0.5}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        mockupConfig: {
                          ...(prev.mockupConfig || {}),
                          centerYRatio: event.target.value,
                        },
                      }))
                    }
                  />
                </label>
                <label>
                  {t("admin.products.radius")}
                  <input
                    type="number"
                    step="0.01"
                    min="0.05"
                    max="0.5"
                    value={form.mockupConfig?.radiusRatio ?? 0.22}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        mockupConfig: {
                          ...(prev.mockupConfig || {}),
                          radiusRatio: event.target.value,
                        },
                      }))
                    }
                  />
                </label>
              </div>
              <label className="full-width">
                {t("admin.products.customDescription")}
                <textarea
                  rows="2"
                  value={form.customDescription || ""}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, customDescription: event.target.value }))
                  }
                />
              </label>
            </div>

            <div className="tag-section">
              <div className="tag-header">
                <h3>{t("admin.products.tags")}</h3>
                <p>{t("admin.products.tagsHelp")}</p>
              </div>
              {PRODUCT_FILTERS.map((filter) => (
                <div className="tag-group" key={filter.key}>
                  <label className="full-width tag-input-label">
                    {t("admin.products.tagLabel", { label: t(`store.filter.${filter.key}`) })}
                    <input
                      type="text"
                      value={formatTagInput(form.tags?.[filter.key])}
                      onChange={(event) => updateTagValues(filter.key, event.target.value)}
                      placeholder={t("admin.products.tagExample", { options: filter.defaultOptions.map(displayTag).join(", ") })}
                    />
                  </label>
                  <span className="tag-suggestions-label">{t("admin.products.quickOptions")}</span>
                  <div className="tag-options">
                    {(tagSuggestions[filter.key] || filter.defaultOptions).map((option) => (
                      <label key={option}>
                        <input
                          type="checkbox"
                          checked={getEditableTagValues(form.tags, filter.key).includes(option)}
                          onChange={() => toggleTagValue(filter.key, option)}
                        />
                        {displayTag(option)}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="image-row">
              <div className="image-field">
                <label>
                  {t("admin.products.mainImageUrl")}
                  <input
                    type="text"
                    value={form.imageUrl}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, imageUrl: event.target.value }))
                    }
                  />
                </label>
                <div className="button-row">
                  <label className="admin-image-upload">
                    {t("admin.products.uploadMainImage")}
                    <input type="file" accept="image/*" aria-label={t("admin.products.uploadMainImage")} onChange={handleMainUpload} />
                  </label>
                  <button
                    type="button"
                    onClick={() => setForm((prev) => ({ ...prev, imageUrl: "" }))}
                  >{t("admin.remove")}</button>
                </div>
              </div>
              {form.imageUrl && <img src={resolveImageUrl(form.imageUrl)} alt={t("admin.products.mainImage")} />}
            </div>

            <div className="color-section">
              <div className="color-header">
                <h3>{t("admin.products.colors")}</h3>
                <button type="button" onClick={addColor}>{t("admin.products.addColor")}</button>
              </div>
              {(form.colors || []).map((color, index) => (
                <div className="color-row" key={`color-${index}`}>
                  <input
                    type="text"
                    placeholder={t("admin.products.colorName")} aria-label={t("admin.products.colorName")}
                    value={color.name || ""}
                    onChange={(event) => updateColor(index, "name", event.target.value)}
                  />
                  <input
                    type="number"
                    step="0.01"
                    placeholder={t("admin.products.price")} aria-label={t("admin.products.price")}
                    value={color.price || ""}
                    onChange={(event) => updateColor(index, "price", event.target.value)}
                  />
                  <input
                    type="text"
                    placeholder={t("admin.products.imageUrl")} aria-label={t("admin.products.imageUrl")}
                    value={color.imageUrl || ""}
                    onChange={(event) => updateColor(index, "imageUrl", event.target.value)}
                  />
                  <label className="admin-image-upload">
                    {t("admin.products.uploadColorImage")}
                    <input type="file" accept="image/*" aria-label={t("admin.products.uploadColorImage")} onChange={(event) => handleColorUpload(index, event)} />
                  </label>
                  <button type="button" onClick={() => updateColor(index, "imageUrl", "")}>{t("admin.products.removeImage")}</button>
                  <button type="button" onClick={() => removeColor(index)}>{t("admin.products.removeColor")}</button>
                  {color.imageUrl && <img src={resolveImageUrl(color.imageUrl)} alt={color.name || t("admin.products.colorImage")} />}
                </div>
              ))}
            </div>

            <div className="button-row">
              <button type="button" onClick={saveProduct}>
                {t(editingId ? "admin.products.update" : "admin.products.create")}
              </button>
              <button type="button" onClick={resetForm}>{t("admin.products.clear")}</button>
            </div>

          </section>

          <section className="list-card">
            <h2>{t("admin.nav.products")}</h2>
            <div className="product-list">
              {products.map((product) => (
                <div className="product-item" key={product.id}>
                  <div>
                    <strong>{product.name}</strong>
                    <span>{t("admin.products.productId", { id: product.id })}</span>
                  </div>
                  <div className="button-row">
                    <button type="button" onClick={() => handleEdit(product)}>{t("admin.edit")}</button>
                    <button type="button" onClick={() => deleteProduct(product.id)}>{t("admin.delete")}</button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default AdminProducts;
